import _ from 'underscore';
import equal from 'fast-deep-equal';
import {makeObservable, action, observable, runInAction, computed} from 'mobx';
import {type State as UppyState} from '@uppy/core';
import type {AnyUppyFile, UppyFileMeta} from '../types';

type SubscriptionFn = (
	prevState: UppyState,
	nextState: UppyState,
	patch: Partial<UppyState>,
) => void;

export class UppyMobxState<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TBody extends Record<string, unknown> = Record<string, unknown>,
> {
	files: UppyState<TMeta, TBody>['files'] = {};
	plugins = {};
	allowNewUpload = false;
	currentUploads = {};
	info?: any;
	meta = {};
}

export interface UppyStateStoreOptions<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TBody extends Record<string, unknown> = Record<string, unknown>,
> {
	/** if not provided, all files will be assumed as pending for uppy */
	fileIsPending?: (file: AnyUppyFile<TMeta, TBody>) => boolean;
}

/**
 * MobX store for uppy.io
 */
export class UppyStateStore<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TBody extends Record<string, unknown> = Record<string, unknown>,
> {
	#callbacks = new Set<SubscriptionFn>();
	state: UppyState<TMeta, TBody> = {
		allowNewUpload: false,
		files: {},
		recoveredState: null,
		totalProgress: 0,
		currentUploads: {},
		info: undefined,
		meta: {},
		plugins: {},
	};

	pendingFiles = observable.array<AnyUppyFile<TMeta, TBody>>([], {
		deep: false,
	});

	get pendingFileCount() {
		return this.pendingFiles.length;
	}

	updatePendingFiles: () => void;

	constructor(opts: UppyStateStoreOptions<TMeta, TBody> = {}) {
		const {fileIsPending} = opts;

		makeObservable(
			this,
			{
				pendingFiles: observable,
				pendingFileCount: computed,
				setState: action,
				// our app will not be reacting to any of uppys internal state, other than
				// the pending files store.
				state: false,
			},
			{
				deep: false,
			},
		);

		this.updatePendingFiles = _.throttle(
			() => {
				const files = Object.values(this.state.files);
				runInAction(() => {
					this.pendingFiles.replace(
						fileIsPending
							? files.filter(fileIsPending)
							: files.filter((file) => file.meta?.uniqueId == null),
					);
				});
			},
			25,
			{trailing: true},
		);
	}

	getState(): UppyState<TMeta, TBody> {
		return {...this.state};
	}

	setState(patch: Partial<UppyState<TMeta, TBody>>) {
		const prevState: UppyState<TMeta, TBody> = {...this.state};
		const nextState: UppyState<TMeta, TBody> = {...prevState, ...patch};

		// in uppy they continually hit `saveFilesStateToLocalStorage` due to there being no files
		// once set state happens, it calls `saveFilesStateToLocalStorage` and this just repeates forever
		// until you add a file
		if (Object.keys(nextState.files)[0] == null && equal(prevState, nextState))
			return;

		Object.assign(this.state, nextState);

		this.#publish(prevState, nextState, patch);
	}

	subscribe(listener: SubscriptionFn) {
		this.#callbacks.add(listener);
		return () => {
			// remove the listener
			this.#callbacks.delete(listener);
		};
	}

	#publish(
		prevState: UppyState<TMeta, TBody>,
		nextState: UppyState<TMeta, TBody>,
		patch: Partial<UppyState<TMeta, TBody>>,
	) {
		if (prevState !== nextState) {
			this.updatePendingFiles();
			this.#callbacks.forEach((listener) =>
				listener(prevState, nextState, patch),
			);
		}
	}

	dispose() {
		this.#callbacks.clear();
	}
}

export default UppyStateStore;
